import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { storesService } from "@/services/storesService";
import { ordersService } from "@/services/ordersService";
import { productsService } from "@/services/productsService";
import { walletsService } from "@/services/walletsService";
import type { AdminStoreBvAdjustDto, StoreUpdateDto, StoreRegisterDto, StoreKycDto } from "@/types/store.dto";
import type { OrderFilterParams, StorePosOrderCreateDto } from "@/types/order.dto";
import type { ProductFormData } from "@/types/product";
import { OrderStatus, WalletOwnerType } from "@/types/enums";
import type { StoreOrderRow } from "@/types/store.ui";
import {
  formDataToProductCreateDto,
  formDataToProductUpdateDto,
  mapOrderListDtosToStoreOrders,
  mapProductListDtosToStoreProducts,
  mapSettlementsToStoreEarnings,
} from "@/lib/mappers/store-mappers";
import { queryKeys } from "./keys";

export function useMyStore(enabled = true) {
  return useQuery({
    queryKey: queryKeys.stores.my,
    queryFn: () => storesService.getMyStore(),
    enabled,
  });
}

export function useStoreId() {
  const { user } = useAuth();
  const needsFetch = !user?.storeId;
  const { data: myStore, isLoading } = useMyStore(needsFetch);

  return {
    storeId: user?.storeId ?? myStore?.id,
    storeName: myStore?.storeName,
    isLoading: needsFetch && isLoading,
  };
}

export function useStoreDashboard(storeId?: number) {
  return useQuery({
    queryKey: queryKeys.stores.dashboard(storeId ?? 0),
    queryFn: () => storesService.getDashboard(storeId!),
    enabled: Boolean(storeId),
  });
}

export function useStoreOrders(storeId?: number, filter: OrderFilterParams = { pageSize: 100 }) {
  return useQuery({
    queryKey: queryKeys.store.orders(storeId ?? 0, filter),
    queryFn: async () => {
      const result = await ordersService.getStoreOrders(storeId!, filter);
      return {
        ...result,
        items: mapOrderListDtosToStoreOrders(result.items),
      };
    },
    enabled: Boolean(storeId),
  });
}

export function useStoreCatalog(storeId?: number, page = 1, pageSize = 100) {
  return useQuery({
    queryKey: queryKeys.store.products(storeId ?? 0, page, pageSize),
    queryFn: async () => {
      const result = await productsService.getByStore(storeId!, page, pageSize);
      return {
        ...result,
        items: mapProductListDtosToStoreProducts(result.items),
      };
    },
    enabled: Boolean(storeId),
  });
}

export function useStoreLowStock(storeId?: number) {
  return useQuery({
    queryKey: queryKeys.store.lowStock(storeId ?? 0),
    queryFn: async () => {
      const items = await productsService.getLowStock(storeId!);
      return mapProductListDtosToStoreProducts(items);
    },
    enabled: Boolean(storeId),
  });
}

export function useStoreEarnings(storeId?: number, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.store.earnings(storeId ?? 0, page, pageSize),
    queryFn: async () => {
      const result = await walletsService.getSettlements({
        storeId,
        ownerType: WalletOwnerType.Store,
        page,
        pageSize,
      });
      return {
        ...result,
        items: mapSettlementsToStoreEarnings(result.items),
      };
    },
    enabled: Boolean(storeId),
  });
}

export function useStoreProduct(productId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.products.detail(productId),
    queryFn: () => productsService.getById(productId),
    enabled: enabled && productId > 0,
  });
}

export function useAdvanceStoreOrderMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      currentStatus,
    }: {
      orderId: number;
      currentStatus: StoreOrderRow["status"];
    }) => {
      switch (currentStatus) {
        case "new":
          return ordersService.confirmOrder(orderId, storeId);
        case "confirmed":
          return ordersService.updateStatus(orderId, { status: OrderStatus.Processing });
        case "preparing":
          return ordersService.markReady(orderId, storeId);
        case "ready":
          return ordersService.updateStatus(orderId, { status: OrderStatus.OutForDelivery });
        case "dispatched":
          return ordersService.updateStatus(orderId, { status: OrderStatus.Delivered });
        default:
          throw new Error("No action available for this order status");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", storeId] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "dashboard"] });
    },
  });
}

export function useLookupPosMemberMutation(storeId: number) {
  return useMutation({
    mutationFn: (memberCode: string) => ordersService.lookupPosMember(storeId, memberCode),
  });
}

export function useCreateStorePosOrderMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: StorePosOrderCreateDto) => ordersService.createStorePosOrder(storeId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "orders"] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "products"] });
    },
  });
}

export function useCreateStoreProductMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductFormData) =>
      productsService.create(storeId, formDataToProductCreateDto(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "products"] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "low-stock"] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "dashboard"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.bv(storeId) });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "bv-history"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
    },
  });
}

export function useUpdateStoreProductMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProductFormData }) =>
      productsService.update(id, storeId, formDataToProductUpdateDto(data)),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "products"] });
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "low-stock"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.bv(storeId) });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "bv-history"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
    },
  });
}

export function useDeleteStoreProductMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => productsService.delete(id, storeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["store", storeId, "products"] });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "dashboard"] });
    },
  });
}

export function useUpdateStoreMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: StoreUpdateDto) => storesService.update(storeId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.detail(storeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.dashboard(storeId) });
    },
  });
}

export function useRegisterStoreMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: StoreRegisterDto) => storesService.registerStore(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
      queryClient.invalidateQueries({ queryKey: ["user", "context"] });
    },
  });
}

export function useUpdateStoreKycMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: StoreKycDto) => storesService.updateKyc(storeId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.detail(storeId) });
    },
  });
}

export function useStoreBvBalance(storeId?: number) {
  return useQuery({
    queryKey: queryKeys.stores.bv(storeId ?? 0),
    queryFn: () => storesService.getBvBalance(storeId!),
    enabled: Boolean(storeId),
  });
}

export function useStoreBvHistory(storeId?: number, page = 1, pageSize = 50) {
  return useQuery({
    queryKey: queryKeys.stores.bvHistory(storeId ?? 0, page, pageSize),
    queryFn: () => storesService.getBvHistory(storeId!, page, pageSize),
    enabled: Boolean(storeId),
  });
}

export function useAdminAdjustStoreBvMutation(storeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: AdminStoreBvAdjustDto) => storesService.adjustBv(storeId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.bv(storeId) });
      queryClient.invalidateQueries({ queryKey: ["stores", storeId, "bv-history"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.detail(storeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.my });
      queryClient.invalidateQueries({ queryKey: ["admin", "store", storeId] });
    },
  });
}
